import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, KeyRound, Loader2, LockKeyhole } from 'lucide-react';
import { requestDemoAccess } from '../services/api';

const GENERIC_ERROR = 'Invalid or unavailable demo access key.';

const DemoAccess = () => {
  const [params] = useSearchParams();
  const [accessKey, setAccessKey] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const loginRequired = params.get('status') === 'login-required';
  const helperText = useMemo(() => {
    if (status === 'rate-limited') return 'Too many attempts. Please try again later.';
    if (status === 'server-error') return 'We could not verify this key right now. Please try again shortly.';
    if (status === 'invalid') return GENERIC_ERROR;
    if (loginRequired) return 'Please enter your private key to continue to the demo.';
    return 'This private concept is provided for review purposes only.';
  }, [loginRequired, status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = accessKey.trim();
    if (!trimmed) {
      setStatus('invalid');
      setMessage(GENERIC_ERROR);
      return;
    }

    setStatus('loading');
    setMessage('');
    try {
      const result = await requestDemoAccess(trimmed);
      if (result?.success && result?.redirect_url) {
        window.location.assign(result.redirect_url);
        return;
      }
      setStatus('invalid');
      setMessage(GENERIC_ERROR);
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 429) {
        setStatus('rate-limited');
      } else if (statusCode >= 500 || !statusCode) {
        setStatus('server-error');
      } else {
        setStatus('invalid');
      }
      setMessage(error?.response?.data?.detail || GENERIC_ERROR);
    }
  };

  const isLoading = status === 'loading';

  return (
    <main className="min-h-screen bg-black text-white flex items-center justify-center px-5 py-12">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute -top-48 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#00FFD1]/10 blur-3xl" />
      </div>

      <section className="relative w-full max-w-md">
        <div
          className="glass-card p-7 sm:p-9"
          style={{
            background: 'rgba(18, 18, 18, 0.86)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '18px',
          }}
        >
          <div className="mb-7 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#00FFD1]/30 bg-[#00FFD1]/15 text-[#00FFD1]">
              <LockKeyhole className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#00FFD1]/80">Webouse private demo</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight">View your private website concept</h1>
            </div>
          </div>

          <p className="mb-6 text-sm leading-6 text-white/62">
            Enter the private access key provided by Webouse.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="demo-access-key" className="mb-2 block text-sm font-medium text-white/72">
                Demo access key
              </label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/36" />
                <input
                  id="demo-access-key"
                  name="demo-access-key"
                  type="password"
                  autoComplete="off"
                  value={accessKey}
                  onChange={(event) => setAccessKey(event.target.value)}
                  className="glass-input pl-12"
                  placeholder="MX-8K4P-27D"
                  disabled={isLoading}
                  aria-describedby="demo-access-help"
                />
              </div>
            </div>

            <button type="submit" disabled={isLoading} className="btn-glass-primary w-full text-base">
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  Open My Demo
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </form>

          <p
            id="demo-access-help"
            className={`mt-5 text-sm leading-6 ${
              status === 'invalid' || status === 'rate-limited' || status === 'server-error'
                ? 'text-red-300'
                : 'text-white/48'
            }`}
            role={status === 'invalid' || status === 'rate-limited' || status === 'server-error' ? 'alert' : undefined}
          >
            {message || helperText}
          </p>
        </div>
      </section>
    </main>
  );
};

export default DemoAccess;
